// Entry point. Ports main() from levelblit.c: boot, asset load,
// title screen menu, then the outer title <-> DungeonPlay loop.

import { initDisplay } from './render/display';
import { initLoop } from './core/loop';
import { initInput } from './core/input';
import { loadAllAssets } from './render/assets';
import { SetGreyscalePalette } from './render/palette';
import { initAudio, enableAudio, ensureMusicPlaying, TitleScreenMusic } from './audio/index';
import { G } from './core/state';
import { createWorld } from './core/world';
import { initContent } from './content/init';
import { enableSceneGuard } from './render/scene';
import { DungeonPlay, cleanupDungeon, setOnSave } from './core/game';
import { loadFromString } from './net/save';
import { initTouch } from './ui/touch';
import { ensureAuth } from './ui/account';
import { initSettings, ShowSettings } from './core/settings';
import { listSaves, loadSlot, logout, saveSlot } from './net/api';
import { initProfiler } from './dev/profiler';
import { initHud, hideGameHud } from './ui/hud';
import {
  initTitleShell,
  setTitleLoading,
  clearTitleLoading,
  hideTitleShell,
  showTitleShell,
  showMainMenu,
} from './ui/title-screen';

const SAVE_KEY = 'meritous_save';

// Save mode: 'cloud' uses the PHP backend; 'offline' uses localStorage.
let saveMode: 'cloud' | 'offline' = 'offline';
let currentUser: string | null = null;
const PRIMARY_SLOT = 0;

function configureSaveMode(mode: 'cloud' | 'offline'): void {
  saveMode = mode;
  if (saveMode === 'cloud') {
    setOnSave((data) => {
      void saveSlot(PRIMARY_SLOT, data).catch(() => undefined);
    });
  } else {
    setOnSave((data) => localStorage.setItem(SAVE_KEY, data));
  }
}

async function hasSave(): Promise<boolean> {
  if (saveMode === 'cloud') {
    try {
      return (await listSaves()).some((s) => s.slot === PRIMARY_SLOT);
    } catch {
      return false;
    }
  }
  return localStorage.getItem(SAVE_KEY) !== null;
}

async function readSave(): Promise<string | null> {
  if (saveMode === 'cloud') {
    try {
      return await loadSlot(PRIMARY_SLOT);
    } catch {
      return null;
    }
  }
  return localStorage.getItem(SAVE_KEY);
}

async function main(): Promise<void> {
  const booted = window as unknown as { __meritous_booted?: boolean };
  if (booted.__meritous_booted) return;
  booted.__meritous_booted = true;

  const root = document.getElementById('game-root')!;
  await initDisplay(root);
  initLoop();
  initInput();
  initAudio();
  initTouch();
  initProfiler();
  createWorld();
  enableSceneGuard();
  initHud(root);
  hideGameHud();

  initTitleShell();

  // Unlock audio on first user gesture (browser autoplay policy).
  const unlock = () => {
    enableAudio();
    ensureMusicPlaying();
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  SetGreyscalePalette();

  await loadAllAssets((p) => {
    setTitleLoading(p, 'Loading... please wait');
  });
  clearTitleLoading();

  await initContent();

  const auth = await ensureAuth();
  currentUser = auth.user;
  configureSaveMode(auth.mode);
  await initSettings(auth.mode);

  for (;;) {
    TitleScreenMusic();
    ensureMusicPlaying();

    const canContinue = await hasSave();
    const choice = await showMainMenu({
      canContinue,
      saveMode,
      user: currentUser,
    });

    if (choice === 'settings') {
      await ShowSettings();
      continue;
    }

    if (choice === 'logout') {
      try {
        await logout();
      } catch {
        // Session may already be expired.
      }
      const nextAuth = await ensureAuth();
      currentUser = nextAuth.user;
      configureSaveMode(nextAuth.mode);
      await initSettings(nextAuth.mode);
      continue;
    }

    hideTitleShell();

    if (choice === 'continue') {
      const data = await readSave();
      if (data) loadFromString(data);
      G.training = 0;
    } else if (choice === 'new') {
      G.training = 0;
    } else if (choice === 'wuss') {
      G.training = 1;
    }

    await DungeonPlay();

    cleanupDungeon();
    showTitleShell();
  }
}

main().catch((e) => {
  const msg = (e && (e as Error).stack) || String(e);
  (window as unknown as { __err: string }).__err = msg;
  console.error(e);
  const root = document.getElementById('game-root');
  if (root) {
    root.innerHTML = '';
    const box = document.createElement('div');
    box.style.cssText =
      'max-width:90vw;padding:24px;color:#ffb0b0;font-family:monospace;font-size:14px;line-height:1.5;white-space:pre-wrap;';
    box.textContent = `Failed to start Meritous:\n\n${msg}`;
    root.appendChild(box);
  }
});
